import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Ferrari F40 (1987) — the mid-engine wedge supercar at true scale
// (1 unit = 1 metre): length ~4.36, width 1.98 (very WIDE), height 1.12
// (very LOW), wheelbase 2.45 (front axle +1.25, rear -1.20), ~0.32 m wheels.
// Nose is +Z. Authored for the `cars` showcase batch.
//
// The silhouette is one continuous LOW WEDGE, never a three-box and never a
// pickup: a low pointed nose with flat rectangular pop-up headlamps -> a
// steeply raked windscreen -> a short low cabin roof -> a recessed LOUVRED
// engine cover sitting in the valley between two tall rear buttresses -> a
// blunt Kamm tail. Above the engine deck stands the signature F40 cue: a wide
// flat rear WING on two vertical uprights, well clear of the deck. The rear
// haunches flare wider than the front, fed by big flank intake scoops ahead of
// the rear wheels. Body panels carry the "paint" recolour slot (Rosso Corsa);
// `drive` spins the wheels and `hazards` blinks four indicators.

const PAINT = "#c00d1e"; // Rosso Corsa — the "paint" slot default
const TRIM = "#141518"; // gloss black (louvres, pillars, wing uprights)
const CLAD = "#202225"; // lower valances, sills, splitter
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2129"; // smoked side / rear glazing
const SCREEN = "#25313c"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const MESH = "#0d0e10"; // engine grille + intake mesh (near-black)
const SPOKE = "#d7dce1"; // bright five-spoke alloys
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#c41726"; // round tail lamps
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). The car is LOW and WIDE — the belt line sits
// at ~0.58 and the roof peaks at ~1.0, while the body runs ~1.8 wide up front
// and flares to a full ~2.0 over the rear haunches, so every square view reads
// clearly wider than tall.
const WHEEL_R = 0.32;
const AXLE_F = 1.25;
const AXLE_R = -1.2; // wheelbase 2.45
const WHEEL_X_F = 0.86; // half track, front — wheels at the corners
const WHEEL_X_R = 0.88; // half track, rear (tucked under the wider haunches)

const SCREEN_RAKE = 0.96; // steeply reclined F40 windscreen (top toward the tail)
const HOOD_SLOPE = 0.093; // frunk lid diving to the low pointed nose
const COVER_SLOPE = 0.21; // louvred engine cover falling toward the tail

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One five-spoke alloy over a low-profile tyre: tyre, brake disc, dark dish,
 *  five bright spokes proud of the tyre face, chrome centre cap. Every part
 *  shares `name` so the `drive` track spins them as one node about the axle.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.06, 18, DISH, [side * (outer - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.055, 0.36, 0.085, SPOKE, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.028, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair flanks the nose intake; rear pair sits beside the tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.62, 0.33, 2.12], size: [0.14, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.62, 0.33, 2.12], size: [0.14, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.8, 0.55, -2.05], size: [0.09, 0.07, 0.03] },
  { name: "indicator-rr", pos: [0.8, 0.55, -2.05], size: [0.09, 0.07, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (wide track — wheels planted at the corners) --------------
  P.push(
    ...wheel(-1, WHEEL_X_F, 0.24, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, 0.24, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, 0.3, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, 0.3, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.9, 0.42, 0.39, 0.05)); // front arch
    P.push(...arch(s, AXLE_R, 0.96, 0.45, 0.42, 0.055)); // flared rear arch, clearly wider
  }
  P.push(box(1.7, 0.12, 3.5, TRIM, [0, 0.2, -0.05])); // underbody pan

  // -- Body masses (paint) — a low wide tub (belt at 0.58) --------------------
  P.push(paint(1.8, 0.36, 3.4, [0, 0.4, -0.05])); // main tub, sill to belt line

  // Low pointed wedge nose: a very low front face, a hood diving to it, and
  // fender crowns beside the hood — the whole top reads as one falling line.
  P.push(paint(1.5, 0.16, 0.14, [0, 0.36, 2.12])); // low front prow face
  P.push(paint(1.5, 0.06, 1.5, [0, 0.52, 1.35], [HOOD_SLOPE, 0, 0])); // frunk lid diving to the nose
  for (const s of S) P.push(paint(0.2, 0.07, 1.5, [s * 0.84, 0.55, 1.35], [HOOD_SLOPE, 0, 0])); // fender crowns
  P.push(box(1.6, 0.1, 0.14, CLAD, [0, 0.23, 2.06])); // front splitter under the prow
  for (const s of S) P.push(box(0.04, 0.02, 0.24, MESH, [s * 0.3, 0.585, 0.95], [HOOD_SLOPE, 0, 0])); // NACA hood ducts

  // -- Rear haunches (the widest, most muscular masses; flare past the cabin) -
  // Two big quarter-panels over the rear wheels; the louvred cover drops into
  // the valley between them, so the deck is never a flat open bed.
  for (const s of S) {
    P.push(paint(0.46, 0.66, 1.5, [s * 0.77, 0.47, -1.05])); // haunch body, outer edge at 1.0
    // Buttress fillet closing the void from the cabin side onto the haunch top.
    P.push(paint(0.24, 0.32, 0.62, [s * 0.6, 0.68, -0.42], [-0.2, 0, 0]));
  }

  // -- Engine bay + louvred cover (fills the valley — never an open deck) -----
  P.push(box(1.1, 0.26, 1.5, MESH, [0, 0.6, -0.7])); // engine bay block under the cover
  P.push(box(1.0, 0.05, 0.95, TRIM, [0, 0.8, -0.8], [-COVER_SLOPE, 0, 0])); // dark cover base
  for (let i = 0; i < 8; i++) {
    const z = -0.4 - 0.1 * i; // from -0.40 back to -1.10
    const y = 0.95 + (z + 0.4) * Math.tan(COVER_SLOPE) + 0.03; // ride the falling slope
    P.push(box(0.92, 0.02, 0.04, MESH, [0, y, z], [-COVER_SLOPE, 0, 0]));
  }
  P.push(paint(1.6, 0.12, 0.6, [0, 0.74, -1.7])); // solid rear deck lip between the haunches

  // -- Greenhouse (low short cabin; one clean raked screen, sealed roof) ------
  P.push(box(1.2, 0.66, 0.04, SCREEN, [0, 0.765, 0.315], [-SCREEN_RAKE, 0, 0])); // windscreen (one clean pane)
  for (const s of S) P.push(paint(0.08, 0.64, 0.08, [s * 0.57, 0.765, 0.32], [-SCREEN_RAKE, 0, 0])); // A-pillars
  P.push(paint(1.22, 0.05, 0.55, [0, 0.975, -0.14])); // roof, edges overlap screen + cover
  P.push(box(1.16, 0.3, 0.85, CABIN, [0, 0.72, -0.05])); // dark interior block
  for (const s of S) {
    P.push(box(0.03, 0.28, 0.6, GLASS, [s * 0.58, 0.76, -0.05], [0, 0, s * 0.06])); // side glass
    P.push(box(0.05, 0.28, 0.06, TRIM, [s * 0.58, 0.76, -0.32], [0, 0, s * 0.06])); // B-pillar
  }
  P.push(box(0.96, 0.03, 0.3, GLASS, [0, 0.9, -0.45], [-COVER_SLOPE, 0, 0])); // rear window on the cover top

  // -- Rear wing: a THIN horizontal blade on two SLIM uprights, in clear air
  //    above the deck — the signature F40 cue, reads floating from every view.
  for (const s of S) {
    P.push(box(0.06, 0.27, 0.1, TRIM, [s * 0.5, 0.925, -1.65])); // slim vertical upright
  }
  P.push(paint(1.5, 0.04, 0.34, [0, 1.05, -1.66])); // thin wing blade
  P.push(box(1.5, 0.02, 0.05, TRIM, [0, 1.04, -1.49])); // blade leading edge
  for (const s of S) P.push(box(0.025, 0.11, 0.34, TRIM, [s * 0.74, 1.04, -1.66])); // end plates

  // -- Flank intake scoops on the haunches (ahead of the rear wheels) --------
  for (const s of S) {
    P.push(box(0.05, 0.26, 0.5, MESH, [s * 1.0, 0.5, -0.6])); // big side intake mouth
    P.push(paint(0.06, 0.05, 0.56, [s * 0.98, 0.65, -0.6])); // scoop upper lip
    P.push(paint(0.06, 0.05, 0.56, [s * 0.98, 0.36, -0.6])); // scoop lower lip
    P.push(paint(0.06, 0.3, 0.06, [s * 0.98, 0.5, -0.35])); // scoop front wall
    P.push(box(0.03, 0.1, 2.6, CLAD, [s * 0.905, 0.28, -0.1])); // rocker sill (reaches both arches)
  }

  // -- Front fascia: flat rectangular pop-up lamps laid on the nose ----------
  for (const s of S) {
    P.push(box(0.36, 0.05, 0.16, TRIM, [s * 0.46, 0.5, 1.9], [HOOD_SLOPE, 0, 0])); // lamp housing (flush pop-up)
    P.push(box(0.32, 0.03, 0.13, LAMP, [s * 0.46, 0.52, 1.9], [HOOD_SLOPE, 0, 0])); // rectangular headlamp
  }
  P.push(box(0.72, 0.12, 0.05, MESH, [0, 0.32, 2.15])); // small front intake, no big grille
  P.push(cyl(0.045, 0.045, 0.02, 16, CHROME, [0, 0.4, 2.19], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.4, 0.09, 0.02, PLATE, [0, 0.29, 2.18])); // small low front plate

  // -- Rear fascia: quad round tail lamps, mesh grille, dark exhausts ---------
  P.push(box(1.7, 0.4, 0.08, TRIM, [0, 0.52, -2.0])); // black rear panel (red deck lip sits above)
  for (const s of S) {
    // two round tail lamps per side on the black panel
    P.push(cyl(0.085, 0.085, 0.05, 16, TAIL_RED, [s * 0.4, 0.58, -2.05], [Math.PI / 2, 0, 0]));
    P.push(cyl(0.085, 0.085, 0.05, 16, TAIL_RED, [s * 0.6, 0.58, -2.05], [Math.PI / 2, 0, 0]));
  }
  // Central black mesh engine grille with vertical slats.
  P.push(box(0.62, 0.24, 0.03, MESH, [0, 0.54, -2.04]));
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.02, 0.2, 0.02, "#26282c", [i * 0.13, 0.54, -2.06]));
  }
  // A single row of three dark gunmetal exhaust tips, centred low.
  for (const dx of [-0.13, 0, 0.13]) {
    P.push(cyl(0.055, 0.055, 0.09, 12, EXHAUST, [dx, 0.34, -2.06], [Math.PI / 2, 0, 0]));
  }
  P.push(box(1.5, 0.14, 0.1, CLAD, [0, 0.24, -2.02])); // rear diffuser valance
  P.push(box(0.4, 0.09, 0.02, PLATE, [-0.42, 0.4, -2.06])); // small rear plate, offset to one side

  // -- Furniture: small mirrors firmly attached to the door tops -------------
  for (const s of S) {
    P.push(box(0.12, 0.03, 0.04, TRIM, [s * 0.66, 0.62, 0.35])); // solid mirror stalk overlapping the body
    P.push(paint(0.06, 0.09, 0.09, [s * 0.74, 0.64, 0.35])); // mirror head
    P.push(box(0.012, 0.07, 0.07, GLASS, [s * 0.775, 0.64, 0.36])); // mirror glass, facing back
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const ferrariF40 = defineModel({
  id: "ferrari-f40",
  name: "Ferrari F40",
  batchId: "cars",
  tags: ["car", "ferrari", "f40", "supercar", "sports", "classic"],
  summary: "A Ferrari F40 mid-engine wedge supercar at true scale, in Rosso Corsa.",
  description:
    "A 1987 Ferrari F40 at real 1 unit = 1 metre scale (about 4.36 long, a " +
    "very wide 1.98, a very low 1.12 tall, 2.45 wheelbase). The signature " +
    "cues are all present: a low pointed wedge nose with flat rectangular " +
    "pop-up headlamps and a small intake, a steeply raked windscreen over a " +
    "short low cabin, a recessed louvred engine cover in the valley between " +
    "two tall rear buttresses, the iconic wide flat rear wing standing on " +
    "two uprights well above the deck, rear haunches flaring wider than the " +
    "front fed by big flank intake scoops, quad round tail lamps on a black " +
    "rear panel with a mesh engine grille and quad central exhausts, and " +
    "bright five-spoke alloys on low-profile tyres. Body panels carry the " +
    "'paint' recolour slot (Rosso Corsa); `drive` spins the wheels and " +
    "`hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.33,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
